/**
 * Indicator Registry — Single source of truth for all report indicators.
 *
 * To rename or reorder an indicator, change ONLY this file.
 * The `legacyKey` must never change (it's stored in saved reports in the DB).
 * The `nameKey` is the i18n translation key.
 * The `order` determines display position within the category.
 */

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const CATEGORIES = [
  {
    id: 'quantitative',
    prefix: '1',
    nameKey: 'Quantitativos',
    headerKey: 'Indicadores quantitativos',
    icon: 'mdi-signal',
  },
  {
    id: 'time',
    prefix: '2',
    nameKey: 'Tempos',
    headerKey: 'Indicadores de tempo médio',
    icon: 'mdi-timer-sand-empty',
  },
  {
    id: 'costs',
    prefix: '3',
    nameKey: 'Custos Manutenção',
    headerKey: 'Indicadores de custos de manutenção',
    icon: 'mdi-currency-usd',
  },
  {
    id: 'quality',
    prefix: '4',
    nameKey: 'Controle de Qualidade',
    headerKey: 'Indicadores de controle de qualidade',
    icon: 'mdi-star',
  },
]

// ---------------------------------------------------------------------------
// Indicators
// ---------------------------------------------------------------------------

export const INDICATORS = [
  // ── Quantitativos (1.x) ──────────────────────────────────────────────
  {
    id: 'equip_by_sector',
    legacyKey: '1.1 - Equipamentos por Setor',
    categoryId: 'quantitative',
    order: 1,
    nameKey: 'Equipamentos por Setor',
    componentName: 'EquipmentBySectorChart',
    apiEndpoint: 'reports/equipmentByDepartment',
    excelEndpoint: 'excel/equipmentByDepartment',
    bigDataEndpoint: 'bigdata/equipmentByDepartment',
    description:
      'Apresenta a quantidade de equipamentos em cada um dos setores da instituição até a data limite filtrada, desconsiderando alienados e acessórios.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'alienated_equipment',
    legacyKey: '1.2 - Equipamentos Desativados no Período',
    categoryId: 'quantitative',
    order: 2,
    nameKey: 'Equipamentos Desativados no Período',
    componentName: 'AlienatedEquip',
    apiEndpoint: 'reports/alienatedEquipment',
    excelEndpoint: 'excel/alienatedEquipment',
    bigDataEndpoint: null,
    description:
      'Apresenta a lista dos equipamentos alienados do sistema no período filtrado.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'request_by_sector',
    legacyKey: '1.3 - Solicitações por Setor',
    categoryId: 'quantitative',
    order: 3,
    nameKey: 'Solicitações por Setor',
    componentName: 'RequestBySector',
    apiEndpoint: 'reports/requestByDepartment',
    excelEndpoint: 'excel/requestByDepartment',
    bigDataEndpoint: 'bigData/requestByDepartment',
    description:
      'Apresenta o número de ordens de serviço de qualquer tipo solicitadas para cada setor do hospital no período filtrado.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'stopped_equip_pending_type',
    legacyKey: '1.4 - Equipamentos Parados x Tipo de Pendencia',
    categoryId: 'quantitative',
    order: 4,
    nameKey: 'Equipamentos Parados x Tipo de Pendencia',
    componentName: 'StoppedEquipPendingType',
    apiEndpoint: 'reports/stoppedEquipmentPendingType',
    excelEndpoint: 'excel/stoppedEquipmentPendingType',
    bigDataEndpoint: null,
    description:
      "Apresenta a lista dos equipamentos com O.S. em aberto, nos quais o campo 'Máquina Parada' está preenchido sem data de retorno.",
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'top_five_failures',
    legacyKey: '1.5 - Top 10 Equipamentos com mais Falhas',
    categoryId: 'quantitative',
    order: 5,
    nameKey: 'Top 10 Equipamentos com mais Falhas',
    componentName: 'TopFiveEquipFailures',
    apiEndpoint: 'reports/topFiveFailures',
    excelEndpoint: 'excel/topFiveEquipmentFailure',
    bigDataEndpoint: null,
    description:
      "Apresenta os 10 equipamentos com maior número de O.S.'s de manutenções corretivas fechadas, elencando as causas das falhas.",
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'defect_solution_prevalence',
    legacyKey: '1.6 - Prevalência Solução Defeito',
    categoryId: 'quantitative',
    order: 6,
    nameKey: 'Prevalência Solução Defeito',
    componentName: 'DefectSolutionPrev',
    apiEndpoint: 'reports/defectSolutionPrevalence',
    excelEndpoint: 'excel/defectSoluctionPrevalence',
    bigDataEndpoint: null,
    description:
      'Apresenta o número de ordens de serviço concluídas no período para cada tipo de solução defeito.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'solicited_v_concluded',
    legacyKey: '1.7 - Serviços Solicitados x Concluídos - Período',
    categoryId: 'quantitative',
    order: 7,
    nameKey: 'Serviços Solicitados x Concluídos - Período',
    componentName: 'SolicitedVConcluded',
    apiEndpoint: 'reports/requestedCompletedServices',
    excelEndpoint: 'excel/requestedCompleted',
    bigDataEndpoint: 'bigData/requestedCompletedServices',
    description:
      'Apresenta a quantidade de serviços solicitados e concluídos no período, distribuídos por tipo de O.S.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'recurring_calls',
    legacyKey: '1.8 - Reincidência de Chamados',
    categoryId: 'quantitative',
    order: 8,
    nameKey: 'Reincidência de Chamados',
    componentName: 'RecurringCalls',
    apiEndpoint: 'reports/recurringCalls',
    excelEndpoint: 'excel/recurringCalls',
    bigDataEndpoint: null,
    description:
      'Lista os equipamentos com mais de um chamado corretivo no período filtrado, ordenados do mais recorrente para o menos.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'corrective_sched_x_exec',
    legacyKey: '1.9 - Corretivas Solicitadas x Executadas - Acumulado',
    categoryId: 'quantitative',
    order: 9,
    nameKey: 'Corretivas Solicitadas x Executadas - Acumulado',
    componentName: 'CorrectiveScheXExec',
    apiEndpoint: 'reports/correctiveScheduleExecuted',
    excelEndpoint: 'excel/correctiveSolicitedVConcluded',
    bigDataEndpoint: 'bigData/correctiveScheduleExecuted',
    description:
      'Apresenta o percentual das corretivas solicitadas que foram realizadas, incluindo as corretivas em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'internal_resolvability',
    legacyKey: '1.10 - Resolutividade Interna',
    categoryId: 'quantitative',
    order: 10,
    nameKey: 'Resolutividade Interna',
    componentName: 'InternalResolvability',
    apiEndpoint: 'reports/internalResoluteness',
    excelEndpoint: 'excel/internalResolvability',
    bigDataEndpoint: 'bigData/internalResoluteness',
    description:
      "Apresenta o número de O.S.'s corretivas fechadas pela equipe interna no período, dividido pelo total de corretivas fechadas no período, sempre em relação aos equipamentos próprios.",
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'preventive_x_corrective',
    legacyKey: '1.11 - Preventiva x Corretiva',
    categoryId: 'quantitative',
    order: 11,
    nameKey: 'Preventiva x Corretiva',
    componentName: 'PreventiveXCorrective',
    apiEndpoint: 'reports/preventiveCorrective',
    excelEndpoint: 'excel/preventiveXCorretive',
    bigDataEndpoint: 'bigData/preventiveCorrective',
    description:
      "Apresenta a quantidade de O.S.'s corretivas fechadas classificadas como Falha Intrínseca de equipamentos com criticidade maior ou igual a 12 e as compara com a quantidade de manutenções preventivas fechadas no mesmo período.",
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'preventive_sched_x_exec',
    legacyKey: '1.12 - Preventivas Agendadas x Executadas - Acumulado',
    categoryId: 'quantitative',
    order: 12,
    nameKey: 'Preventivas Agendadas x Executadas - Acumulado',
    componentName: 'PreventiveSchedXExec',
    apiEndpoint: 'reports/preventiveScheduleExecuted',
    excelEndpoint: 'excel/preventiveScheduleExecuted',
    bigDataEndpoint: 'bigData/preventiveScheduleExecuted',
    description:
      'Apresenta o percentual de execução das preventivas programadas no período filtrado, incluindo as preventivas em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'calib_sched_x_exec',
    legacyKey: '1.13 - Calibrações Agendadas x Executadas - Acumulado',
    categoryId: 'quantitative',
    order: 13,
    nameKey: 'Calibrações Agendadas x Executadas - Acumulado',
    componentName: 'CalibSchedXExec',
    apiEndpoint: 'reports/calibrationScheduleExecuted',
    excelEndpoint: 'excel/calibrationScheduleXExecuted',
    bigDataEndpoint: 'bigData/calibrationScheduleExecuted',
    description:
      'Apresenta o percentual de execução das calibrações programadas no período filtrado, incluindo as calibrações em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'eletric_sec_sched_x_exec',
    legacyKey: '1.14 - Segurança Elétrica Agendada x Executada - Acumulado',
    categoryId: 'quantitative',
    order: 14,
    nameKey: 'Segurança elétrica agendada x executada - Acumulado',
    componentName: 'EletricSecSchedXExec',
    apiEndpoint: 'reports/electricalSafetyScheduleExceuted',
    excelEndpoint: 'excel/eletricalSafetyScheduleXExecuted',
    bigDataEndpoint: 'bigData/electricalSafetyScheduleExceuted',
    description:
      'Apresenta o percentual de execução dos testes de segurança elétrica programados no período filtrado, incluindo os testes de segurança elétrica em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'validation_qualification_sched_x_exec',
    legacyKey:
      '1.15 - Validação/Qualificação Solicitadas x Executadas - Acumulado',
    categoryId: 'quantitative',
    order: 15,
    nameKey: 'Validação/Qualificação Solicitadas x Executadas - Acumulado',
    componentName: 'ValidationQualificationSchedXExec',
    apiEndpoint: 'reports/validationQualificationScheduleExecuted',
    excelEndpoint: 'excel/validationQualificationScheduleXExecuted',
    bigDataEndpoint: null,
    description:
      'Apresenta o percentual de execução das validações/qualificações programadas no período filtrado, incluindo as validações/qualificações em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'scheduled_maintenance',
    legacyKey: '1.16 - Manutenções Programadas - Acumulado',
    categoryId: 'quantitative',
    order: 16,
    nameKey: 'Manutenções Programadas - Acumulado',
    componentName: 'ScheduledMaintenance',
    apiEndpoint: 'reports/scheduledMaintenanceExecuted',
    excelEndpoint: 'excel/preventiveScheduleExecuted',
    bigDataEndpoint: null,
    description:
      'Apresenta o percentual de execução das manutenções programadas (preventiva, calibração, segurança elétrica e validação/qualificação) no período filtrado, incluindo as manutenções programadas dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'historical_migration',
    legacyKey: '1.17 - Histórico de Migração de Equipamentos',
    categoryId: 'quantitative',
    order: 17,
    nameKey: 'Histórico de Migração de Equipamentos',
    componentName: 'HistoricalMigration',
    apiEndpoint: 'reports/historicalMigration',
    excelEndpoint: 'excel/historicalMigration',
    bigDataEndpoint: null,
    description:
      'Exibe a lista dos equipamentos que foram transferidos para outros setores ou unidades no período filtrado.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'order_by_workshop',
    legacyKey: '1.18 - Solicitações por Oficina',
    categoryId: 'quantitative',
    order: 18,
    nameKey: 'Solicitações por Oficina',
    componentName: 'OrderByWorkshop',
    apiEndpoint: 'reports/orderByWorkshop',
    excelEndpoint: 'excel/requestByWorkshop',
    bigDataEndpoint: null,
    description:
      'Apresenta o número de ordens de serviço solicitadas para cada oficina do hospital no período filtrado.',
    visibleWhen: (institution) => {
      const types = institution?.departmentTypes ?? []
      return types.includes(1)
    },
    extraProps: [],
  },
  {
    id: 'responsible_services_by_week',
    legacyKey: '1.19 - Ordens de serviço por responsável - Período',
    categoryId: 'quantitative',
    order: 19,
    nameKey: 'Ordens de serviço por responsável - Período',
    componentName: 'ResponsibleServicesByWeek',
    apiEndpoint: 'reports/responsibleServicesByWeek',
    excelEndpoint: 'excel/responsibleServicesByWeek',
    bigDataEndpoint: null,
    description:
      "Indica quantas O.S.'s foram solicitadas e concluídas por cada usuário no período selecionado.",
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'requests_per_cost_center',
    legacyKey: '1.19 - Solicitações por Centro de Custo',
    categoryId: 'quantitative',
    order: 20,
    nameKey: 'Solicitações por Centro de Custo',
    componentName: 'RequestsPerCostCenter',
    apiEndpoint: 'reports/requestsByCostCenter',
    excelEndpoint: 'excel/requestsByCostCenter',
    bigDataEndpoint: null,
    description:
      'Apresenta o número de ordens de serviço de qualquer tipo solicitadas para cada centro de custo no período filtrado.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'user_cause_defect',
    legacyKey: '1.21 - Uso Inadequado de Equipamentos',
    categoryId: 'quantitative',
    order: 21,
    nameKey: 'Uso Inadequado de Equipamentos',
    componentName: 'InappropriateUseEquipment',
    apiEndpoint: 'reports/inappropriateUseEquipment',
    excelEndpoint: 'excel/inappropriateUseEquipment',
    bigDataEndpoint: null,
    description:
      'Apresenta a distribuição das O.S.s corretivas fechadas no período por causa relacionada ao usuário: Erro Operacional, Abuso na Utilização e Dano Acidental.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'inspection_sched_x_exec',
    legacyKey: '1.22 - Rotinas de Inspeção x Executadas - Acumulado',
    categoryId: 'quantitative',
    order: 22,
    nameKey: 'Rotinas de Inspeção x Executadas - Acumulado',
    componentName: 'InspectionSchedXExec',
    apiEndpoint: 'reports/inspectionRoutineScheduleExecuted',
    excelEndpoint: 'excel/inspectionRoutineScheduleXExecuted',
    bigDataEndpoint: null,
    description:
      'Apresenta o percentual de execução das rotinas de inspeção programadas no período filtrado, incluindo as rotinas de inspeção em aberto dos períodos anteriores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'pending_os_by_type',
    legacyKey: '1.23 - Pendências por Tipo de O.S.',
    categoryId: 'quantitative',
    order: 23,
    nameKey: 'Pendências por Tipo de O.S.',
    componentName: 'PendingOsByType',
    apiEndpoint: 'reports/pendingOsByType',
    excelEndpoint: 'excel/pendingOsByType',
    bigDataEndpoint: null,
    description:
      'Apresenta a quantidade de O.S.s pendentes (não fechadas) no período filtrado, agrupadas por tipo de O.S. (Corretiva, Preventiva, Calibração, Teste de Segurança Elétrica e Rotina de Inspeção) e por execução interna ou externa, com faixas de tempo de pendência: menos de 30 dias, mais de 30 dias e mais de 90 dias. Inclui gráfico de pizza com a distribuição por tipo de pendência.',
    visibleWhen: null,
    extraProps: [],
  },

  // ── Tempos (2.x) ─────────────────────────────────────────────────────
  {
    id: 'stopped_machine',
    legacyKey: '2.1 - Tempo de máquina parada',
    categoryId: 'time',
    order: 1,
    nameKey: 'Tempo de máquina parada',
    componentName: 'StoppedMachine',
    apiEndpoint: 'reports/machineDowntime',
    excelEndpoint: 'excel/equipmentDowmtime',
    bigDataEndpoint: null,
    description:
      'Apresenta o tempo de parada dos equipamentos filtrados no período.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'stand_equip_failures',
    legacyKey: '2.2 - Tempo médio entre falhas por equipamento padrão',
    categoryId: 'time',
    order: 2,
    nameKey: 'Tempo médio entre falhas por equipamento padrão',
    componentName: 'StandEquipFailures',
    apiEndpoint: 'reports/failuresStandardEquipment',
    excelEndpoint: 'excel/standartEquipmentFailures',
    bigDataEndpoint: null,
    description:
      'Apresenta a média de tempo entre as manutenções corretivas classificadas como falha intrínseca, organizadas por equipamento padrão no período selecionado.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'total_service_time',
    legacyKey: '2.3 - Tempo médio entre falhas por equipamento',
    categoryId: 'time',
    order: 3,
    nameKey: 'Tempo médio entre falhas por equipamento',
    componentName: 'TotalServiceTime',
    apiEndpoint: 'reports/failuresEquipment',
    excelEndpoint: 'excel/equipmentFailures',
    bigDataEndpoint: null,
    description:
      'Apresenta a média de tempo entre as manutenções corretivas classificadas como falha intrínseca, organizadas por ID dos equipamentos no período selecionado.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'service_forecast',
    legacyKey: '2.4 - Atendimento x Previsão atendimento',
    categoryId: 'time',
    order: 4,
    nameKey: 'Atendimento x Previsão atendimento',
    componentName: 'ServiceForecast',
    apiEndpoint: 'reports/serviceForecast',
    excelEndpoint: 'excel/serviceForecast',
    bigDataEndpoint: null,
    description:
      'Apresenta uma relação dos equipamentos e setores que não tiveram seus tempos de atendimento dentro das metas por criticidade estipuladas, de acordo com a data de solicitação da corretiva.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'calls_answered',
    legacyKey: '2.5 - Chamados atendidos no prazo',
    categoryId: 'time',
    order: 5,
    nameKey: 'Chamados Corretivos Atendidos no Prazo',
    componentName: 'CallsAnsweredChart',
    apiEndpoint: 'reports/callsAnswered',
    excelEndpoint: 'excel/callsAnsweredChart',
    bigDataEndpoint: null,
    description:
      'Apresenta o percentual de chamados corretivos atendidos dentro do prazo por faixa de criticidade.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'resolution_forecast',
    legacyKey: '2.6 - Resolução x Previsão resolução',
    categoryId: 'time',
    order: 6,
    nameKey: 'Resolução x Previsão resolução',
    componentName: 'ResolutionForecast',
    apiEndpoint: 'reports/resolutionForecast',
    excelEndpoint: 'excel/resolutionForecast',
    bigDataEndpoint: null,
    description:
      'Apresenta uma relação dos equipamentos e setores que não tiveram seus tempos de resolução dentro das metas por criticidade estipuladas, de acordo com a data de fechamento da corretiva.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'calls_resolved',
    legacyKey: '2.7 - Chamados resolvidos no prazo',
    categoryId: 'time',
    order: 7,
    nameKey: 'Chamados Corretivos Resolvidos no Prazo',
    componentName: 'CallsResolvedChart',
    apiEndpoint: 'reports/callsResolved',
    excelEndpoint: 'excel/callsResolvedChart',
    bigDataEndpoint: null,
    description:
      'Apresenta o percentual de chamados corretivos resolvidos dentro do prazo por faixa de criticidade.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'service_time_department',
    legacyKey: '2.8 - Tempo de atendimento interno por setor',
    categoryId: 'time',
    order: 8,
    nameKey: 'Tempo de atendimento interno por setor',
    componentName: 'ServiceTimeDepartment',
    apiEndpoint: 'reports/serviceTimeByDepartment',
    excelEndpoint: 'excel/serviceTimeDepartment',
    bigDataEndpoint: null,
    description:
      "Apresenta uma média de tempo entre as solicitações e os atendimentos efetivos das O.S.'s de manutenção corretiva solicitadas no período filtrado, separadas por setor.",
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'service_time_criticality',
    legacyKey: '2.9 - Tempo de atendimento por criticidade',
    categoryId: 'time',
    order: 9,
    nameKey: 'Tempo de atendimento por criticidade',
    componentName: 'ServiceTimeCrit',
    apiEndpoint: 'reports/serviceTimeByCriticalty',
    excelEndpoint: 'excel/serviceTimeCriticality',
    bigDataEndpoint: null,
    description:
      "Apresenta uma média de tempo entre as solicitações e os atendimentos efetivos das O.S.'s de manutenção corretiva solicitadas no período filtrado, separadas pela faixa de criticidade dos equipamentos.",
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'productive_time',
    legacyKey: '2.10 - Tempo produtivo',
    categoryId: 'time',
    order: 10,
    nameKey: 'Tempo produtivo',
    componentName: 'ProdTime',
    apiEndpoint: 'reports/productiveTime',
    excelEndpoint: 'excel/productiveTime',
    bigDataEndpoint: null,
    description:
      'Apresenta o somatório do tempo total despendido em atendimento para cada colaborador da equipe técnica, registrado em tarefas e ordens de serviço do período filtrado.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'productive_time_sector',
    legacyKey: '2.11 - Tempo produtivo por setor',
    categoryId: 'time',
    order: 11,
    nameKey: 'Tempo produtivo por setor',
    componentName: 'ProdTimeSector',
    apiEndpoint: 'reports/productiveTimeSector',
    excelEndpoint: 'excel/productiveTimeSector',
    bigDataEndpoint: null,
    description:
      'Apresenta o somatório do tempo total despendido em atendimento para cada setor, registrado em tarefas e ordens de serviço no período filtrado. O tempo total deve ser equivalente ao indicador acima.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'resolution_time_criticality',
    legacyKey: '2.12 - Tempo médio de resolução interna por criticidade',
    categoryId: 'time',
    order: 12,
    nameKey: 'Tempo médio de resolução interna por criticidade',
    componentName: 'ResolutionTimeCrit',
    apiEndpoint: 'reports/resolutionTimeByCriticalty',
    excelEndpoint: 'excel/resolutionTimeCriticality',
    bigDataEndpoint: null,
    description:
      'Apresenta uma média de tempo de resolução dos atendimentos corretivos de acordo com a criticidade dos equipamentos no período filtrado.',
    visibleWhen: null,
    extraProps: ['standardEquipments'],
  },
  {
    id: 'total_resolution_time_criticality',
    legacyKey: '2.13 - Tempo Médio Total de Resolução por Criticidade',
    categoryId: 'time',
    order: 13,
    nameKey: 'Tempo Médio Total de Resolução por Criticidade',
    componentName: 'TotalResolutionTimeCrit',
    apiEndpoint: 'reports/resolutionTimeByCriticaltyWithoutExternalDiscount',
    excelEndpoint: 'excel/resolutionTimeCriticalityWithoutExternalDiscount',
    bigDataEndpoint: null,
    description:
      'Apresenta uma média de tempo de resolução dos atendimentos corretivos de acordo com a criticidade dos equipamentos no período filtrado, sem desconto de tempo externo.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'service_orders_time_steps',
    legacyKey: '2.14 - Tempo por Etapa da O.S.',
    categoryId: 'time',
    order: 14,
    nameKey: 'Tempo por Etapa da O.S.',
    componentName: 'ServiceOrdersTimeSteps',
    apiEndpoint: 'reports/servicesOrdersStepTimes',
    excelEndpoint: 'excel/servicesOrdersStepTimesExcel',
    bigDataEndpoint: null,
    description:
      'Apresenta o somatório e a média dos tempos gastos das ordens de serviço fechadas por etapa.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'displacement_time',
    legacyKey: '2.15 - Tempo de deslocamento',
    categoryId: 'time',
    order: 15,
    nameKey: 'Tempo de deslocamento',
    componentName: 'DisplacementTime',
    apiEndpoint: 'reports/displacementTime',
    excelEndpoint: 'excel/displacementTimeExcel',
    bigDataEndpoint: null,
    description:
      'Apresenta as O.S. com etapa de Deslocamento no período, com o tempo total e médio gasto nessa etapa.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'productive_time_order_type',
    legacyKey: '2.16 - Tempo Produtivo por tipo de O.S.',
    categoryId: 'time',
    order: 16,
    nameKey: 'Tempo Produtivo por tipo de O.S.',
    componentName: 'ProdTimeOrderType',
    apiEndpoint: 'reports/productiveTimeByOrderType',
    excelEndpoint: 'excel/productiveTimeByOrderType',
    bigDataEndpoint: null,
    description:
      'Apresenta, para cada tipo de O.S., os técnicos com etapas lançadas no período, a quantidade de etapas preenchidas, o tempo médio e o tempo total das etapas.',
    visibleWhen: null,
    extraProps: [],
  },

  // ── Custos Manutenção (3.x) ──────────────────────────────────────────
  {
    id: 'cost_per_piece',
    legacyKey: '3.1 - Custos por peça avulsa',
    categoryId: 'costs',
    order: 1,
    nameKey: 'Custos por peça avulsa',
    componentName: 'CostPerPiece',
    apiEndpoint: 'reports/listCostsSinglePiece',
    excelEndpoint: 'excel/costSinglePiece',
    bigDataEndpoint: null,
    description: 'Apresenta os custos gerados por trocas de peças.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'cost_per_service',
    legacyKey: '3.2 - Custos por serviço avulso',
    categoryId: 'costs',
    order: 2,
    nameKey: 'Custos por serviço avulso',
    componentName: 'CostPerService',
    apiEndpoint: 'reports/listCostsSingleService',
    excelEndpoint: 'excel/costPerService',
    bigDataEndpoint: null,
    description:
      'Apresenta os custos gerados por serviços executados por terceiros.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'cost_per_sector',
    legacyKey: '3.3 - Custo por setor',
    categoryId: 'costs',
    order: 3,
    nameKey: 'Custo por setor',
    componentName: 'CostPerSector',
    apiEndpoint: 'reports/listCostsDepartment',
    excelEndpoint: 'excel/costPerSector',
    bigDataEndpoint: null,
    description:
      'Apresenta os custos gerados por peças, serviços e contratos distribuídos por cada setor.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'cost_per_equipment',
    legacyKey: '3.4 - Custo por ID equipamento',
    categoryId: 'costs',
    order: 4,
    nameKey: 'Custo por ID equipamento',
    componentName: 'CostPerId',
    apiEndpoint: 'reports/listCostsEquipment',
    excelEndpoint: 'excel/costPeriod',
    bigDataEndpoint: null,
    description: 'Apresenta o custo com serviço e peças por equipamento.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'logistic_cost',
    legacyKey: '3.5 - Custo Logístico',
    categoryId: 'costs',
    order: 5,
    nameKey: 'Custo Logístico',
    componentName: 'LogisticCost',
    apiEndpoint: 'reports/listCostsLogistic',
    excelEndpoint: 'excel/costPerLogistic',
    bigDataEndpoint: null,
    description:
      'Apresenta os custos logísticos gerados por equipe interna ou fornecedores.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'avoided_costs',
    legacyKey: '3.6 - Custo evitado',
    categoryId: 'costs',
    order: 6,
    nameKey: 'Custo Evitado',
    componentName: 'AvoidedCosts',
    apiEndpoint: 'reports/listAvoidedCosts',
    excelEndpoint: 'excel/avoidedCost',
    bigDataEndpoint: null,
    description: 'Apresenta os custos evitados pela manutenção preventiva.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'internal_labor_cost',
    legacyKey: '3.7 - Custo de Mão de Obra Interna',
    categoryId: 'costs',
    order: 7,
    nameKey: 'Custo de Mão de Obra Interna',
    componentName: 'InternalLaborCost',
    apiEndpoint: 'reports/internalLaborCost',
    excelEndpoint: 'excel/internalLaborCost',
    bigDataEndpoint: null,
    description: 'Apresenta o custo de mão de obra interna calculado pelo tempo gasto em etapas das ordens de serviço multiplicado pelo valor homem-hora de cada responsável.',
    visibleWhen: null,
    extraProps: [],
  },

  // ── Controle de Qualidade (4.x) ──────────────────────────────────────
  {
    id: 'order_ratings',
    legacyKey: '4.1 - Notas das avaliações O.S.',
    categoryId: 'quality',
    order: 1,
    nameKey: 'Notas das avaliações de Chamados',
    componentName: 'OrderRatings',
    apiEndpoint: 'reports/ratingServiceOrder',
    excelEndpoint: 'excel/serviceOrderRatings',
    bigDataEndpoint: null,
    description:
      'Apresenta as avaliações dos chamados realizados, definidas em: 1 – Ruim, 2 – Regular, 3 – Bom, 4 – Ótima.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'ranking_supplier',
    legacyKey: '4.2 - Ranking fornecedor',
    categoryId: 'quality',
    order: 2,
    nameKey: 'Ranking fornecedor',
    componentName: 'RankingSupplier',
    apiEndpoint: 'reports/rankingSupplier',
    excelEndpoint: 'excel/rankingSupplier',
    bigDataEndpoint: null,
    description:
      'Apresenta a média das notas dadas ao tempo de atendimento e conclusão do serviço de fornecedores selecionados.',
    visibleWhen: null,
    extraProps: [],
  },
  {
    id: 'adhesion_called_module',
    legacyKey: '4.3 - Adesão ao módulo de chamados',
    categoryId: 'quality',
    order: 3,
    nameKey: 'Adesão ao módulo de chamados',
    componentName: 'AdhesionCalledMod',
    apiEndpoint: 'reports/adhesionCalled',
    excelEndpoint: 'excel/adhesionCalled',
    bigDataEndpoint: null,
    description:
      'Apresenta a relação em porcentagem entre os chamados de manutenção corretiva feitos via módulo de chamados e o total de chamados de manutenção corretivas lançados no sistema.',
    visibleWhen: null,
    extraProps: [],
  },
]

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

const _byId = new Map(INDICATORS.map((i) => [i.id, i]))
const _byLegacyKey = new Map(INDICATORS.map((i) => [i.legacyKey, i]))

/**
 * Get an indicator by its stable ID.
 * @param {string} id
 * @returns {object|undefined}
 */
export function getIndicatorById(id) {
  return _byId.get(id)
}

/**
 * Get an indicator by its legacy key (e.g. '1.1 - Equipamentos por Setor').
 * Used for backward compatibility with saved reports.
 * @param {string} key
 * @returns {object|undefined}
 */
export function getIndicatorByLegacyKey(key) {
  return _byLegacyKey.get(key)
}

/**
 * Get a category by its ID.
 * @param {string} categoryId
 * @returns {object|undefined}
 */
export function getCategoryById(categoryId) {
  return CATEGORIES.find((c) => c.id === categoryId)
}

/**
 * Get visible indicators for a category, with computed `displayNumber`.
 * Filters based on institution visibility conditions and assigns sequential numbers.
 * @param {string} categoryId
 * @param {object} institution - institution object from localStorage
 * @returns {Array<object>} indicators with added `displayNumber` field
 */
export function getVisibleIndicators(categoryId, institution) {
  const indicators = INDICATORS.filter((i) => i.categoryId === categoryId)
    .sort((a, b) => a.order - b.order)
    .filter((i) => !i.visibleWhen || i.visibleWhen(institution))

  return indicators.map((ind, index) => ({
    ...ind,
    displayNumber: index + 1,
  }))
}

/**
 * Get all visible indicators across all categories, with computed `displayNumber`.
 * @param {object} institution
 * @returns {Array<object>}
 */
export function getAllVisibleIndicators(institution) {
  return CATEGORIES.flatMap((cat) => getVisibleIndicators(cat.id, institution))
}

/**
 * Build the display name of an indicator ("1.20 - Nome"), using the same
 * dynamic numbering shown in the UI. Used for Excel export filenames, which
 * must not rely on `legacyKey` — that number is frozen for DB compatibility
 * and does not follow the position the user actually sees.
 * @param {string} id
 * @param {object} institution
 * @returns {string}
 */
export function getIndicatorExportName(id, institution) {
  const indicator = getIndicatorById(id)
  if (!indicator) return ''

  const cat = getCategoryById(indicator.categoryId)
  const visible = getVisibleIndicators(indicator.categoryId, institution)
  const match = visible.find((i) => i.id === id)
  const number = match ? match.displayNumber : indicator.order

  return `${cat.prefix}.${number} - ${indicator.nameKey}`
}

/**
 * Get indicators from a selection array that belong to a specific category.
 * @param {string} categoryId
 * @param {Array<string>} selection - array of legacyKey strings
 * @param {object} institution
 * @returns {Array<object>} indicators with `displayNumber`
 */
export function getSelectedIndicatorsForCategory(
  categoryId,
  selection,
  institution
) {
  const visible = getVisibleIndicators(categoryId, institution)
  return visible.filter((ind) => selection.includes(ind.legacyKey))
}

/**
 * Get categories that have at least one selected indicator.
 * @param {Array<string>} selection
 * @param {object} institution
 * @returns {Array<object>}
 */
export function getCategoriesWithSelection(selection, institution) {
  return CATEGORIES.filter(
    (cat) =>
      getSelectedIndicatorsForCategory(cat.id, selection, institution).length >
      0
  )
}

/**
 * Build the chartsValues map used in result.vue for display numbering.
 * Maps legacyKey → displayNumber.
 * @param {Array<string>} selection
 * @param {object} institution
 * @returns {object}
 */
export function buildChartsValuesMap(selection, institution) {
  const map = {}
  for (const cat of CATEGORIES) {
    const visible = getVisibleIndicators(cat.id, institution)
    const selected = visible.filter((ind) => selection.includes(ind.legacyKey))
    selected.forEach((ind, index) => {
      map[ind.legacyKey] = index + 1
    })
  }
  return map
}

/**
 * Get categories that have at least one item in chartInfos.
 * @param {Array<object>} chartInfos - array with `chart` property (legacyKey)
 * @param {object} institution
 * @returns {Array<object>}
 */
export function getCategoriesWithChartInfos(chartInfos, institution) {
  const chartKeys = chartInfos.map((ci) => ci.chart)
  return CATEGORIES.filter((cat) => {
    const visible = getVisibleIndicators(cat.id, institution)
    return visible.some((ind) => chartKeys.includes(ind.legacyKey))
  })
}

/**
 * Get indicators from chartInfos for a specific category, with displayNumber.
 * @param {string} categoryId
 * @param {Array<object>} chartInfos
 * @param {object} institution
 * @returns {Array<object>}
 */
export function getChartInfoIndicatorsForCategory(
  categoryId,
  chartInfos,
  institution
) {
  const chartKeys = chartInfos.map((ci) => ci.chart)
  const visible = getVisibleIndicators(categoryId, institution)
  return visible.filter((ind) => chartKeys.includes(ind.legacyKey))
}
